import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import usePaginationState from "./paginationState";

const _defaultPageSizeOptions = [10, 20, 30];

const _defaultPaginationModel = {
  pageSize: _defaultPageSizeOptions[0],
  page: 0,
};

function getDefaultPaginationModel(overridePageSizeOptions) {
  return overridePageSizeOptions && overridePageSizeOptions.length > 0
    ? { ..._defaultPaginationModel, pageSize: overridePageSizeOptions[0] }
    : _defaultPaginationModel;
}

const _defaultFilterModel = {
  items: [],
  logicOperator: "and",
};

export function buildQueryModel(paginationModel, sortModel, filterModel, searchText) {
  return {
    searchText: searchText,
    pagination: paginationModel
      ? {
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
        }
      : null,
    filter: filterModel
      ? {
          items: filterModel.items.map((f) => ({
            ...f,
            value: f.value && Array.isArray(f.value) ? null : f.value,
            valueArray: f.value && Array.isArray(f.value) ? f.value : null,
          })),
          logicOperator: filterModel.logicOperator,
        }
      : null,
    sort: sortModel
      ? sortModel.map((sm) => ({
          field: sm.field,
          direction: sm.sort,
        }))
      : null,
  };
}

/**
 * The callback that loads the data for usePagination. Will be called each time page, sort, filter or searchText changes.
 *
 * @callback loadDataCallback
 * @param {Object} paginationRequest - The request sent to backend, matching ePortWebLibrary/Database/Models/Pagination/QueryModel.cs
 * @returns {Promise<{rowCount: number, rows: Object[]}>}
 */
/**
 * usePagination will generate props for a DataGrid to make it use server-side pagination.
 * @param {loadDataCallback} loadData - The callback that loads the data to be displayed in the DataGrid
 * @returns {Object} The DataGrid props. Use spread operator into the DataGrid for ex: <DataGrid {...pagination} />
 */
export default function usePagination(
  loadData,
  searchText,
  defaultSortProp,
  defaultFilterProp,
  defaultPageSizeOptionsProp,
  paginationStateId,
  overrideFilterProp,
  scrollOnPageChange
) {
  const [defaultSort] = useState(defaultSortProp);
  const [defaultFilter] = useState(defaultFilterProp);
  const [defaultPageSizeOptions] = useState(defaultPageSizeOptionsProp);
  const [overrideFilter] = useState(overrideFilterProp);

  const [hasLoadedPersistedState, setHasLoadedPersistedState] = useState(false);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState(getDefaultPaginationModel(defaultPageSizeOptions));
  const [filterModel, setFilerModel] = useState(overrideFilter ?? defaultFilter ?? _defaultFilterModel);
  const [sortModel, setSortModel] = useState(defaultSort ? defaultSort : []);
  const [rowCount, setRowCount] = useState(0);
  const [pageSizeOptions] = useState(defaultPageSizeOptions ?? _defaultPageSizeOptions);

  const { savePersistedState, loadPersistedState, clearPersistedState } = usePaginationState(paginationStateId);

  const handleLoadData = useCallback(
    async (paginationModel, sortModel, filterModel, searchText) => {
      setIsLoading(true);
      const response = await loadData(buildQueryModel(paginationModel, sortModel, filterModel, searchText));
      if(response && response.rows) {
        const { rows, rowCount } = await loadData(buildQueryModel(paginationModel, sortModel, filterModel, searchText));
        setRows(rows);
        setRowCount(rowCount);
        setIsLoading(false);
      }
    },
    [loadData]
  );

  useEffect(() => {
    if (!hasLoadedPersistedState) {
      const persistedState = loadPersistedState();
      if (
        persistedState.paginationModel ||
        persistedState.sortModel ||
        (!!overrideFilter ? false : persistedState.filterModel)
      ) {
        if (
          persistedState.paginationModel &&
          persistedState.paginationModel.pageSize &&
          !pageSizeOptions.includes(persistedState.paginationModel.pageSize)
        ) {
          // The implementation has changed and pageSizeOptions differ
          // from what was used when we saved to sessionStorage last time
          clearPersistedState();
        } else {
          setPaginationModel(persistedState.paginationModel);
          setSortModel(persistedState.sortModel);
          setFilerModel(overrideFilter ?? persistedState.filterModel);
        }
      }
      setHasLoadedPersistedState(true);
    }
  }, [hasLoadedPersistedState, pageSizeOptions, overrideFilter, loadPersistedState, clearPersistedState]);

  useEffect(() => {
    if (hasLoadedPersistedState) {
      handleLoadData(paginationModel, sortModel, filterModel, searchText);
      savePersistedState(paginationModel, sortModel, filterModel);
    }
  }, [
    handleLoadData,
    paginationModel,
    sortModel,
    filterModel,
    searchText,
    hasLoadedPersistedState,
    savePersistedState,
  ]);

  const onFilterChange = useCallback(async (filterModel) => {
    setFilerModel(filterModel ?? _defaultFilterModel);
  }, []);

  const onSortChange = useCallback(
    async (sortModels) => {
      setSortModel(sortModels ?? (defaultSort ? defaultSort : []));
    },
    [defaultSort]
  );

  const onPaginationChange = useCallback(
    async (paginationModel) => {
      setPaginationModel((prev) => {
        const newPaginationModel = paginationModel ?? getDefaultPaginationModel(defaultPageSizeOptions);
        if (scrollOnPageChange && newPaginationModel.page !== prev.page) {
          window.scrollTo({ top: 0 });
        }
        return newPaginationModel;
      });
    },
    [defaultPageSizeOptions, scrollOnPageChange]
  );

  const reloadData = useCallback(
    async () => await handleLoadData(paginationModel, sortModel, filterModel, searchText),
    [handleLoadData, paginationModel, sortModel, filterModel, searchText]
  );

  const resetPagination = useCallback(() => {
    onPaginationChange(null);
    onSortChange(null);
    onFilterChange(null);
  }, [onFilterChange, onSortChange, onPaginationChange]);

  return {
    rows: rows,
    pageSizeOptions: pageSizeOptions,
    pagination: true,
    paginationModel: paginationModel,
    filterMode: "server",
    sortingMode: "server",
    paginationMode: "server",
    filterModel: filterModel,
    sortModel: sortModel,
    onFilterModelChange: onFilterChange,
    onSortModelChange: onSortChange,
    onPaginationModelChange: onPaginationChange,
    loading: isLoading,
    rowCount: rowCount,
    localUpdateRows: setRows,
    reloadData: reloadData,
    resetPagination: resetPagination,
  };
}
